import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import ShareButton from "@/components/ShareButton";
import { Newspaper, ArrowLeft, Award, GraduationCap, Calendar } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" }) : "");

function embedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return null;
}

export default function Article() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/articles/${slug}`).then(({ data }) => {
      setData(data);
      const a = data.article;
      document.title = (a.seo?.seo_title || a.title) + " · CBR Prospects";
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <PublicLayout><div className="max-w-3xl mx-auto p-16 text-center"><h1 className="font-display text-3xl uppercase">Article not found</h1><Link to="/news" className="text-[#E63946] underline mt-3 inline-block">← Back to News</Link></div></PublicLayout>;
  if (!data) return <PublicLayout><div className="max-w-3xl mx-auto p-16 label-overline">Loading…</div></PublicLayout>;

  const a = data.article;
  const embed = embedUrl(a.video_url);
  const players = a.associations_detail?.players || [];
  const camps = a.associations_detail?.camps || [];

  return (
    <PublicLayout>
      <article>
        <section className="relative bg-[#0A192F] text-white border-b-4 border-[#E63946]">
          {a.cover_image_url && <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url(${a.cover_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />}
          <div className="relative max-w-3xl mx-auto px-6 lg:px-8 py-16">
            <Link to="/news" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-xs uppercase tracking-wider"><ArrowLeft className="w-4 h-4" /> News</Link>
            <div className="label-overline text-[#E63946] mt-4">{a.category?.replace(/_/g, " ")}</div>
            <h1 data-testid="article-title" className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mt-2">{a.title}</h1>
            {a.subtitle && <p className="text-white/70 text-lg mt-3">{a.subtitle}</p>}
            <div className="text-xs text-white/50 mt-5 flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> {fmt(a.published_at)} · {a.author_name}</div>
          </div>
        </section>

        <section className="bg-white py-12">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            {embed && <div className="aspect-video mb-8"><iframe src={embed} title="video" className="w-full h-full rounded-sm" allowFullScreen /></div>}
            <div data-testid="article-body" className="prose prose-slate max-w-none prose-headings:font-display prose-headings:uppercase prose-a:text-[#E63946]" dangerouslySetInnerHTML={{ __html: a.body_html }} />

            <div className="mt-8"><ShareButton title={a.title} label="Share Article" /></div>

            {(players.length > 0 || camps.length > 0) && (
              <div className="mt-10 pt-8 border-t border-slate-200" data-testid="article-related">
                <div className="label-overline text-[#E63946] mb-3">Featured In This Story</div>
                <div className="flex flex-wrap gap-2">
                  {players.map((p) => (
                    <Link key={p.id} to={`/prospects/${p.slug}`} className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-sm text-sm"><GraduationCap className="w-3.5 h-3.5 text-[#E63946]" />{p.first_name} {p.last_name}</Link>
                  ))}
                  {camps.map((c) => (
                    <Link key={c.id} to={`/camps/${c.slug}/results`} className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-sm text-sm"><Award className="w-3.5 h-3.5 text-[#E63946]" />{c.title}</Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {data.related?.length > 0 && (
          <section className="bg-[#F8F9FA] py-12 border-t border-slate-200">
            <div className="max-w-5xl mx-auto px-6 lg:px-8">
              <h2 className="label-overline text-[#0A192F] mb-4">More {a.category?.replace(/_/g, " ")}</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {data.related.map((r) => (
                  <Link key={r.id} to={`/news/${r.slug}`} className="group bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-[#0A192F] transition-colors">
                    <div className="aspect-[16/9] bg-[#0A192F] relative">{r.cover_image_url ? <img src={r.cover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 grid place-items-center"><Newspaper className="w-6 h-6 text-white/20" /></div>}</div>
                    <div className="p-3"><h3 className="text-sm font-semibold group-hover:text-[#E63946] line-clamp-2">{r.title}</h3></div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </PublicLayout>
  );
}
